// Featured AI / Data Science projects sourced from Karim's Great Learning
// e-portfolio (MIT Applied AI & Data Science Program, Sep 2025 – Feb 2026).
// Data is curated locally so the section is fast & SEO-friendly; the card
// link points to the live e-portfolio for the full evidence.

import { ArrowUpRight, Brain, Sparkles, BarChart3 } from "lucide-react";
import { useI18n } from "@/lib/i18nContext";

export const GREAT_LEARNING_URL =
  "https://www.mygreatlearning.com/eportfolio/karim-benna";

const PROJECTS = [
  {
    key: "retail-genai",
    icon: Sparkles,
    date: "Jan 2026",
    courseKey: "capstone",
    skills: [
      "Sentiment Analysis",
      "Generative AI",
      "Prompt Engineering",
      "Text Classification",
      "Text Summarization",
    ],
  },
  {
    key: "svhn-cnn",
    icon: Brain,
    date: "Dec 2025",
    courseKey: "deepLearning",
    skills: [
      "Deep Learning",
      "ANN",
      "CNN",
      "Image Classification",
      "Computer Vision",
      "Model Evaluation",
    ],
  },
  {
    key: "foodhub-eda",
    icon: BarChart3,
    date: "Oct 2025",
    courseKey: "foundations",
    skills: [
      "Exploratory Data Analysis",
      "Data Visualization",
      "NumPy",
      "Pandas",
    ],
  },
];

export default function FeaturedProjects() {
  const { t } = useI18n();

  return (
    <section
      id="featured-projects"
      data-testid="featured-projects"
      className="relative border-t hairline bg-[var(--bg-0)] scroll-mt-24 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 relative z-10">
        <div className="mb-12 lg:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("projects.label")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-3xl">
              {t("projects.title")}{" "}
              <span className="font-serif gold-grad">{t("projects.titleAccent")}</span>
              {t("projects.titleEnd")}
            </h2>
            <p className="mt-5 text-[var(--text-1)] text-base lg:text-lg leading-relaxed max-w-2xl">
              {t("projects.intro")}
            </p>
          </div>

          {/* Program badge */}
          <a
            href={GREAT_LEARNING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="projects-program-badge"
            className="group inline-flex items-center gap-3 px-4 py-3 rounded border hairline-gold bg-[var(--bg-1)]/70 backdrop-blur-sm transition-all duration-200 hover:border-[var(--gold)]/60 hover:bg-[var(--bg-1)] shrink-0"
          >
            <div className="text-left">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--text-2)]">
                {t("projects.programLabel")}
              </div>
              <div className="font-display text-sm text-[var(--text-0)] leading-snug mt-0.5">
                {t("projects.programName")}
              </div>
              <div className="font-mono text-[11px] text-[var(--text-2)] mt-0.5">
                Sep 2025 – Feb 2026
              </div>
            </div>
            <ArrowUpRight
              size={16}
              className="text-[var(--gold)] transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {PROJECTS.map((p, idx) => {
            const Icon = p.icon;
            return (
              <a
                key={p.key}
                href={GREAT_LEARNING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`project-${p.key}`}
                className="svc-card group flex flex-col relative overflow-hidden"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center">
                    <Icon size={22} className="text-[var(--gold)]" />
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-2)] block">
                      {p.date}
                    </span>
                    <span className="font-mono text-[10px] text-[var(--text-2)]/70 block mt-1">
                      0{idx + 1} / 03
                    </span>
                  </div>
                </div>

                <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-2">
                  {t(`projects.items.${p.key}.course`)}
                </div>
                <h3 className="font-display text-xl lg:text-2xl text-[var(--text-0)] leading-snug mb-4">
                  {t(`projects.items.${p.key}.title`)}
                </h3>
                <p className="text-[var(--text-1)] text-sm leading-relaxed mb-6 flex-1">
                  {t(`projects.items.${p.key}.desc`)}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-5">
                  {p.skills.map((s) => (
                    <span
                      key={s}
                      className="text-[11px] font-mono px-2.5 py-1 rounded border hairline text-[var(--text-1)] bg-[var(--bg-0)]/60"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-[var(--gold)] mt-auto group-hover:text-[var(--text-0)] transition-colors duration-200">
                  <span>{t("projects.viewLink")}</span>
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
              </a>
            );
          })}
        </div>

        {/* Full portfolio CTA */}
        <div className="mt-12 flex justify-center">
          <a
            href={GREAT_LEARNING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="projects-full-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border hairline-gold text-[var(--gold)] hover:bg-[var(--gold)]/10 transition-all duration-200 text-sm font-mono uppercase tracking-[0.18em]"
          >
            {t("projects.fullPortfolio")}
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
